import {
  Attachments,
  InvestigationQuestionTypeEnum,
  InvestigationReportingControllerFindAllInjuriesIncidentClassificationEnum,
  InvestigationReportingControllerFindAllInjuriesIncidentTypeOfLossEnum,
  InvestigationTypeEnum,
} from '@EHSNavigator/backend';
import * as Yup from 'yup';

export const getInvestigationReportingSchema = (
  step: number,
  max: Attachments[],
) => {
  if (step === 1) {
    const existingFiles = max || [];
    const remainingAllowed = 3 - existingFiles.length;
    return Yup.object().shape({
      injuryIllness: Yup.string().required('Injuray/Illness is required'),
      bodyPartAffected: Yup.array()
        .of(Yup.string())
        .min(1, 'At least one body part must be selected')
        .required('Body Part Affected is required'),
      typeOfLoss: Yup.string().required('Type of Loss is required'),
      classification: Yup.string().required('Classification is required'),
      classifyIllness: Yup.string().when('injuryIllness', {
        is: (val: string) => val === InvestigationTypeEnum.Illness,
        then: (schema) => schema.required('Classify Illness is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
      daysAwayFromWork: Yup.number().when('typeOfLoss', {
        is: (val: string) =>
          val ===
          InvestigationReportingControllerFindAllInjuriesIncidentTypeOfLossEnum.DaysAwayFromWork,
        then: (schema) =>
          schema
            .required('Days Away From Work is required')
            .moreThan(0, 'Days Away From Work must be greater than zero')
            .test(
              'is-integer',
              'Days Away From Work must be a whole number',
              (value) =>
                value === undefined ||
                value === null ||
                Number.isInteger(value),
            ),
        otherwise: (schema) => schema.notRequired(),
      }),

      jobRestrictionDays: Yup.number().when('typeOfLoss', {
        is: (val: string) =>
          val ===
          InvestigationReportingControllerFindAllInjuriesIncidentTypeOfLossEnum.RestrictedWorkActivityOrJobTransfer,
        then: (schema) =>
          schema
            .required('Job Restriction Days is required')
            .moreThan(0, 'Job Restriction Days must be greater than zero')
            .test(
              'is-integer',
              'Job Restriction Days must be a whole number',
              (value) =>
                value === undefined ||
                value === null ||
                Number.isInteger(value),
            ),
        otherwise: (schema) => schema.notRequired(),
      }),
      dateOfDeath: Yup.string().when('typeOfLoss', {
        is: (val: string) =>
          val ===
          InvestigationReportingControllerFindAllInjuriesIncidentTypeOfLossEnum.Death,
        then: (schema) => schema.required('Date of death is required'),
        otherwise: (schema) => schema.notRequired(),
      }),
      files: Yup.array()
        .max(remainingAllowed, `Maximum 3 files allowed`)
        .of(
          Yup.mixed()
            .test('is-file', 'Value must be a file', (value) => {
              if (!value) return true;
              return typeof File !== 'undefined' && value instanceof File;
            })
            .test('fileSize', 'File too large', function (value) {
              const { path, createError } = this;
              if (!value || !(value instanceof File)) return true;

              const isVideo = value.type.startsWith('video/');
              const maxSize = isVideo ? 50 * 1024 * 1024 : 5 * 1024 * 1024;

              if (value.size > maxSize) {
                return createError({
                  path,
                  message: `${value.name} exceeds the maximum size limit of ${isVideo ? '50MB (video)' : '5MB'}`,
                });
              }

              if (
                existingFiles
                  ?.map((node: Attachments) => node?.name)
                  ?.includes(value.name)
              ) {
                return createError({
                  path,
                  message: `${value.name} already exists in attachments`,
                });
              }

              return true;
            }),
        ),
    });
  } else if (step === 2) {
    return Yup.object().shape({
      fiveWhysQuestionAnswer: Yup.array().of(
        Yup.object().shape({
          question: Yup.string().test(
            'question-required-based-on-files',
            'Question is required',
            function (value) {
              const {
                options: { context },
              } = this;
              const files = context?.files || [];
              const existingAttachments = max || [];
              const totalFiles = [...files, ...existingAttachments];
              if (totalFiles.length === 0) {
                return Boolean(value && value.trim().length > 0);
              }
              return true;
            },
          ),
          answer: Yup.string()
            .trim()
            .test(
              'answer-required-based-on-files',
              'Answer is required',
              function (value) {
                const {
                  options: { context },
                } = this;
                const files = context?.files || [];
                const existingAttachments = max || [];
                const totalFiles = [...files, ...existingAttachments];
                if (totalFiles.length === 0) {
                  return Boolean(value && value.trim().length > 0);
                }
                return true;
              },
            ),
        }),
      ),
      treatmentFacilityType: Yup.string().when(
        ['classification', 'typeOfLoss'],
        {
          is: (classification: string, typeOfLoss: string) =>
            classification ===
              InvestigationReportingControllerFindAllInjuriesIncidentClassificationEnum.Hospitalized ||
            typeOfLoss ===
              InvestigationReportingControllerFindAllInjuriesIncidentTypeOfLossEnum.MedicalTreatmentBeyondFirstAid,
          then: (schema) => schema.required('Treatment Facility is required'),
          otherwise: (schema) => schema.notRequired(),
        },
      ),
      narBeforeIncident: Yup.string().required('Answer is required'),
      narWhatHappened: Yup.string().required('Answer is required'),
      narObjectSubstance: Yup.string().required('Answer is required'),
    });
  } else if (step === 3) {
    return Yup.object().shape({
      customQuestions: Yup.array().of(
        Yup.object().shape({
          questionId: Yup.string().required('Question ID is required'),
          answer: Yup.string().required('Answer is required'),
        }),
      ),
    });
  } else if (step === 4) {
    return Yup.object().shape({
      rootCauseAnalysis: Yup.string().required(
        'Root Cause Analysis is required',
      ),
      otherQuestions: Yup.array().of(
        Yup.object().shape({
          questionType: Yup.string().required('Question Type is required'),
          question: Yup.string().required('Question is required'),
          answer: Yup.string().required('Answer is required'),
          options: Yup.array()
            .of(Yup.string())
            .when('questionType', {
              is: (val: string) =>
                val === InvestigationQuestionTypeEnum?.MultiOptions,
              then: (schema) =>
                schema
                  .min(1, 'At least one option is required')
                  .required('Options are required')
                  .test('unique', 'Options must be unique', (value) => {
                    if (!value) return true;
                    const lowerCased = value.map((v) =>
                      v?.toLowerCase?.().trim?.(),
                    );
                    return new Set(lowerCased).size === lowerCased.length;
                  }),
              otherwise: (schema) => schema.notRequired(),
            }),
        }),
      ),
    });
  } else {
    return Yup.object();
  }
};
